import { TruckIcon } from '@heroicons/react/24/outline';
import { SecondaryButton } from '@shared/components/buttons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { EmptyState } from '../../../components/empty-state';
import { PrimaryLinkButton } from '../../../components/link-buttons';
import { SearchInput } from '../../../components/search-input';
import {
  integrationLabels,
  isGenericAiVendor,
  type Vendor,
  type VendorIntegration,
} from '../../../data/vendor';
import { AiReceivingPill } from './ai-receiving-pill';
import { FormatPill } from './format-pill';
import { invoiceFormatLabel } from './invoice-formats';
import { StepHeading } from './step-heading';

export type VendorPickerProps = {
  vendors: Vendor[];
  /** Integrations catalog, used to name a group the way the Vendors page does. */
  integrations: VendorIntegration[];
  storeId: string;
  disabled?: boolean;
  onSubmit: (vendorIds: string[]) => Promise<void> | void;
};

type VendorGroup = {
  /** Stable key: the shared normalized name, or `solo:<vendor_id>` for ungrouped. */
  key: string;
  /** Heading shown on the card — the integration name, or the vendor's own name. */
  label: string;
  members: Vendor[];
};

/**
 * Groups eligible vendors by shared normalized_name. An empty normalized_name
 * NEVER groups — stores carry hundreds of un-integrated vendor records, and
 * treating '' as a key would build one enormous group. Those become solo cards.
 */
export function groupVendors(
  vendors: Vendor[],
  integrations: VendorIntegration[] = []
): VendorGroup[] {
  const labelByName = integrationLabels(integrations);
  const byName = new Map<string, Vendor[]>();
  const solo: VendorGroup[] = [];

  for (const v of vendors) {
    if (!v.normalized_name) {
      solo.push({ key: `solo:${v.vendor_id}`, label: v.name, members: [v] });
      continue;
    }

    const existing = byName.get(v.normalized_name);
    if (existing) {
      existing.push(v);
    } else {
      byName.set(v.normalized_name, [v]);
    }
  }

  const grouped = [...byName.entries()].map(([name, members]) => ({
    key: name,
    // A one-member group reads better under the vendor's own name than under
    // the integration. For a real group, use the integration's catalog label —
    // the same wording the Vendors page shows in its integration dropdown —
    // never the raw normalized_name, which is an internal code (`ARMA_OK`).
    label: members.length === 1 ? members[0].name : (labelByName.get(name) ?? name),
    // Alphabetize the checklist itself — a 10-member group in arbitrary API
    // order reads badly, and nothing downstream depends on member order (the
    // backend picks its anchor independently, by lexicographically-least id).
    members: [...members].sort((a, b) => a.name.localeCompare(b.name)),
  }));

  return [...grouped, ...solo].sort((a, b) => a.label.localeCompare(b.label));
}

export function VendorPicker({
  vendors,
  integrations,
  storeId,
  disabled = false,
  onSubmit,
}: VendorPickerProps) {
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);
  // Per-group deselections. Absent group = every member selected.
  const [deselected, setDeselected] = useState<Record<string, Set<string>>>({});

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // Not sorted by name here: groupVendors owns member ordering (alphabetizes
  // within each group) and card ordering (alphabetizes by label) itself, so
  // a pre-sort here would be redundant.
  const eligible = useMemo(
    () => vendors.filter((v) => !v.disabled && (v.invoice_formats.length > 0 || v.accepts_ai_csv)),
    [vendors]
  );

  const groups = useMemo(() => groupVendors(eligible, integrations), [eligible, integrations]);

  if (eligible.length === 0) {
    return (
      <EmptyState
        icon={<TruckIcon className="size-8" />}
        title="No vendors set up for receiving"
        description="Set up a Vendor Integration on a vendor to receive its invoices here."
        action={
          <PrimaryLinkButton to="/stores/$storeId/vendors" params={{ storeId }}>
            Go to Vendors
          </PrimaryLinkButton>
        }
      />
    );
  }

  const needle = query.trim().toLowerCase();
  const visible = needle
    ? groups.filter(
        (g) =>
          g.label.toLowerCase().includes(needle) ||
          g.members.some((m) => m.name.toLowerCase().includes(needle))
      )
    : groups;

  const selectedIds = (g: VendorGroup) => {
    const off = deselected[g.key];

    return g.members.filter((m) => !off?.has(m.vendor_id)).map((m) => m.vendor_id);
  };

  const toggle = (g: VendorGroup, vendorId: string) => {
    setDeselected((prev) => {
      const off = new Set(prev[g.key] ?? []);
      if (off.has(vendorId)) {
        off.delete(vendorId);
      } else {
        // Never let the last member be unticked — an empty set can't be submitted.
        if (g.members.length - off.size <= 1) {
          return prev;
        }

        off.add(vendorId);
      }

      return { ...prev, [g.key]: off };
    });
  };

  const only = (g: VendorGroup, vendorId: string) => {
    setDeselected((prev) => ({
      ...prev,
      [g.key]: new Set(g.members.map((m) => m.vendor_id).filter((id) => id !== vendorId)),
    }));
  };

  const handleSubmit = (g: VendorGroup) => {
    if (submittingKey) {
      return;
    }

    setSubmittingKey(g.key);
    void Promise.resolve(onSubmit(selectedIds(g))).catch(() => setSubmittingKey(null));
  };

  const rowsDisabled = disabled || submittingKey !== null;

  return (
    <div className="flex flex-col gap-4 min-h-0">
      <StepHeading>Pick a vendor</StepHeading>
      <div className="flex items-center">
        <SearchInput
          inputRef={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={rowsDisabled}
        />
      </div>
      {visible.length === 0 ? (
        <p className="text-sm text-gray-500">No vendors match &ldquo;{query}&rdquo;.</p>
      ) : (
        <div className="flex flex-col gap-1 overflow-y-auto min-h-0">
          {visible.map((g) => {
            const chosen = selectedIds(g);
            const isMulti = g.members.length > 1;
            const isSubmitting = submittingKey === g.key;

            // A single-vendor card carries no internal controls, so the whole
            // card stays the click target — one tap to pick, as before groups
            // existed. Only a real group needs a separate submit, because its
            // checkboxes make the card itself ambiguous to click.
            if (!isMulti) {
              return (
                // biome-ignore lint/a11y/useSemanticElements: radio button with rich content (name + pills) cannot use <input type="radio">
                <button
                  key={g.key}
                  type="button"
                  role="radio"
                  aria-checked={isSubmitting}
                  onClick={() => handleSubmit(g)}
                  disabled={rowsDisabled}
                  className={`w-full text-left rounded-sm border-2 p-3 transition cursor-pointer disabled:cursor-not-allowed ${
                    isSubmitting
                      ? 'border-primary-500'
                      : 'border-gray-200 hover:border-primary-400 disabled:hover:border-gray-200'
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900">
                    {g.label}
                    {isSubmitting && (
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        Setting vendor&hellip;
                      </span>
                    )}
                  </p>
                  <div className="flex gap-1 mt-1">
                    {isGenericAiVendor(g.members[0]) ? (
                      <AiReceivingPill />
                    ) : (
                      g.members[0].invoice_formats.map((f) => (
                        <FormatPill key={f} label={invoiceFormatLabel(f)} />
                      ))
                    )}
                  </div>
                </button>
              );
            }

            return (
              <div key={g.key} className="rounded-sm border-2 border-gray-200 p-3">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-sm font-medium text-gray-900">{g.label}</p>
                  <p className="text-xs text-gray-500">
                    {chosen.length} of {g.members.length} selected
                  </p>
                </div>

                <ul className="mt-2 flex flex-col gap-1">
                  {g.members.map((m) => {
                    const on = chosen.includes(m.vendor_id);

                    return (
                      <li key={m.vendor_id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`${g.key}-${m.vendor_id}`}
                          checked={on}
                          disabled={rowsDisabled}
                          onChange={() => toggle(g, m.vendor_id)}
                          className="size-4 rounded-sm border-gray-300"
                        />
                        <label
                          htmlFor={`${g.key}-${m.vendor_id}`}
                          className="text-sm text-gray-700 grow cursor-pointer"
                        >
                          {m.name}
                        </label>
                        <button
                          type="button"
                          disabled={rowsDisabled}
                          onClick={() => only(g, m.vendor_id)}
                          aria-label={`Only ${m.name}`}
                          className="text-xs text-primary-600 hover:underline disabled:text-gray-400"
                        >
                          only
                        </button>
                      </li>
                    );
                  })}
                </ul>

                <div className="flex items-center justify-between gap-2 mt-3">
                  <div className="flex gap-1">
                    {isGenericAiVendor(g.members[0]) ? (
                      <AiReceivingPill />
                    ) : (
                      g.members[0].invoice_formats.map((f) => (
                        <FormatPill key={f} label={invoiceFormatLabel(f)} />
                      ))
                    )}
                  </div>
                  <SecondaryButton
                    type="button"
                    disabled={rowsDisabled}
                    onClick={() => handleSubmit(g)}
                  >
                    {isSubmitting ? 'Setting vendor…' : 'Receive selected'}
                  </SecondaryButton>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
